import GenericForm from "@/components/cms/GenericForm";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import { SheetClose } from "@/components/ui/sheet";
import { FieldConfig } from "@/config/forms/general";
import { Edit2, PlusCircle } from "lucide-react";
import { useState } from "react";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

type LinksDrawerLayoutProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  fields: FieldConfig[];
  recordId: string | undefined | null;
  onSubmit: (data: T) => void;
};

const LinksDrawerLayout = <T extends FieldValues>({
  form,
  fields,
  recordId,
  onSubmit,
}: LinksDrawerLayoutProps<T>) => {
  const { t } = useTranslation();
  const { getValues, control, handleSubmit } = form;

  const [open, setOpen] = useState(false);

  const localHandleSubmit = (data: T) => {
    try {
      onSubmit(data);
      setTimeout(() => {
        setOpen(false);
      }, 1000);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <StyledDrawer
      sheetProps={{
        open: open,
        onOpenChange: setOpen,
      }}
      title={recordId ? t("editLinkTitle") : t("createLinkTitle")}
      className="flex flex-col justify-between overflow-y-auto"
      trigger={
        <Button className="font-semibold flex gap-2 p-1" variant="ghost">
          {recordId ? (
            <Edit2 className="w-5 h-5 cursor-pointer text-gray-500" />
          ) : (
            <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
          )}
        </Button>
      }
      footer={
        <CustomDialogFooter>
          <SheetClose asChild>
            <Button className="font-semibold w-full border" variant="ghost">
              {t("cancel")}
            </Button>
          </SheetClose>
          <Button
            className="font-semibold w-full"
            variant="default"
            onClick={handleSubmit(localHandleSubmit)}
          >
            {recordId ? t("editLink") : t("createLink")}
          </Button>
        </CustomDialogFooter>
      }
    >
      <FormProvider {...form}>
        <GenericForm
          getValues={getValues}
          control={control}
          configuration={{ fields: fields ?? [], onSubmit: () => {} }}
        />
      </FormProvider>
    </StyledDrawer>
  );
};

export default LinksDrawerLayout;
